import { CSSProperties, ReactNode, useCallback, useState } from 'react'

import { useMediaQuery } from '@sequencehq/utils'
import { useAtomValue } from 'jotai'
import { match } from 'ts-pattern'
import {
  IconEyeOpen,
  BorderRadius6,
  GreyWhite,
  Primary12SemiBold,
  ShadowM,
  Button,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useLocation } from 'react-router-dom'
import {
  clientInvoiceAtom,
  humanReadableInvoiceNumberAtom,
  invoiceIdAtom,
  invoiceStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { PdfPreviewDrawer } from 'Invoices/components/PdfPreviewDrawer'
import { invoicePaths } from 'Invoices/invoices.router'
import { InvoiceRenderer } from '../../InvoiceRenderer'
import { previewContainerRefAtom } from '../preview.atoms'
import { SHOW_PREVIEW_TO_SIDE_MIN_WIDTH } from '../../constants'
import { useIsInvoiceEditable } from 'Invoices/hooks/useIsInvoiceEditable'

const PDF_PREVIEW_WIDTHS: Record<string, number> = {
  base: 704,
  previewToSideMinWidth: 466,
  xl: 555,
  '2xl': 667
}

const usePdfPreviewWidth = () => {
  const [isPreviewToSide, is2xl, isXl] = useMediaQuery([
    `(min-width: ${SHOW_PREVIEW_TO_SIDE_MIN_WIDTH}) and (max-width: 80em)`,
    '(min-width: 96em)',
    '(min-width: 80em)'
  ])

  if (isPreviewToSide) return PDF_PREVIEW_WIDTHS.previewToSideMinWidth
  if (is2xl) return PDF_PREVIEW_WIDTHS['2xl']
  if (isXl) return PDF_PREVIEW_WIDTHS.xl
  return PDF_PREVIEW_WIDTHS.base
}

export const PdfPreview = () => {
  const { pathname } = useLocation()
  const width = usePdfPreviewWidth()
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const isEditable = useIsInvoiceEditable()
  const previewContainerRef = useAtomValue(previewContainerRefAtom)
  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const invoiceNumber = useAtomValue(humanReadableInvoiceNumberAtom)

  const [buttonContainerStyle, setButtonContainerStyle] = useState<
    CSSProperties | undefined
  >(undefined)

  const overlayOnMouseOver = useCallback(() => {
    if (!previewContainerRef) return

    const { top, left, width, height } =
      previewContainerRef.getBoundingClientRect()

    const isScrollable =
      previewContainerRef.scrollHeight > previewContainerRef.clientHeight ||
      previewContainerRef.scrollWidth > previewContainerRef.clientWidth

    if (!isScrollable) {
      setButtonContainerStyle(undefined)
      return
    }

    setButtonContainerStyle({
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
      position: 'fixed'
    })
  }, [previewContainerRef])

  const showViewAllPagesOnHover: boolean = !pathname.startsWith(
    invoicePaths.new()
  )

  return (
    <Flex
      position="relative"
      width="fit-content"
      marginX="auto"
      className={showViewAllPagesOnHover ? 'group' : undefined}
      data-testid="invoice.react-preview"
    >
      <Page variant="preview">
        <InvoiceRenderer
          width={width ?? PDF_PREVIEW_WIDTHS.base}
          showSkeletons={isEditable || !clientInvoice}
        />

        {showViewAllPagesOnHover && (
          <HoverOverlay
            onClick={() => setIsOpen(true)}
            onMouseOver={overlayOnMouseOver}
          >
            <Center style={buttonContainerStyle} pointerEvents="none">
              <ViewAllPagesButton />
            </Center>
          </HoverOverlay>
        )}
      </Page>

      <Page variant="stack">
        <HoverOverlay />
      </Page>

      {isOpen && (
        <PdfPreviewDrawer
          closeModal={() => setIsOpen(false)}
          invoiceId={invoiceId}
          invoiceStatus={invoiceStatus}
          invoiceNumber={invoiceNumber}
        />
      )}
    </Flex>
  )
}

const Page = ({
  variant,
  children
}: {
  variant: 'preview' | 'stack'
  children: ReactNode
}) => {
  const layoutStyle = match(variant)
    .with(
      'preview',
      () =>
        ({
          width: 'fit-content',
          height: 'fit-content',
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden'
        }) as const
    )
    .with(
      'stack',
      () =>
        ({
          width: 'calc(100% - 10px)',
          height: '20px',
          position: 'absolute',
          bottom: '-5px',
          left: '5px'
        }) as const
    )
    .exhaustive()

  return (
    <Box
      {...layoutStyle}
      borderRadius={BorderRadius6}
      border="1px solid rgba(0, 0, 0, 0.08)"
      boxShadow={ShadowM}
      background={GreyWhite}
    >
      {children}
    </Box>
  )
}

const HoverOverlay = ({
  children,
  onClick,
  onMouseOver
}: {
  children?: ReactNode
  onClick?: () => void
  onMouseOver?: () => void
}) => {
  return (
    <Center
      width="100%"
      height="100%"
      background="rgba(0, 0, 0, 0.12)"
      position="absolute"
      top="0px"
      left="0px"
      opacity={0}
      _groupHover={{ opacity: 1 }}
      transition="opacity 250ms"
      onClick={onClick}
      cursor={onClick ? 'pointer' : undefined}
      onMouseOver={onMouseOver}
    >
      {children}
    </Center>
  )
}

const ViewAllPagesButton = () => {
  return (
    <Button
      variant="unstyled"
      leadingIcon={<IconEyeOpen color="inherit" />}
      style={{
        ...Primary12SemiBold,
        borderRadius: BorderRadius6,
        color: GreyWhite,
        background: 'rgba(0, 0, 0, 0.61)',
        padding: '8px 10px',
        pointerEvents: 'none'
      }}
      data-testid="invoice.react-preview.view-all-pages-button"
    >
      View all pages
    </Button>
  )
}
